export type SiteLocale = "en" | "el";

const GREEK_PREFIX = "/el";
const NON_PUBLIC_PREFIXES = [
  "/admin",
  "/api",
  "/contracts",
  "/media",
  "/office",
  "/static",
  "/studio",
];

export function getSiteLocale(pathname?: string): SiteLocale {
  const value = pathname ?? (typeof window === "undefined" ? "/" : window.location.pathname);
  return value === GREEK_PREFIX || value.startsWith(`${GREEK_PREFIX}/`) ? "el" : "en";
}

export function stripLocalePrefix(pathname: string) {
  if (pathname === GREEK_PREFIX || pathname === `${GREEK_PREFIX}/`) return "/";
  if (pathname.startsWith(`${GREEK_PREFIX}/`)) return pathname.slice(GREEK_PREFIX.length) || "/";
  return pathname;
}

export function localizeHref(href: string, locale: SiteLocale = getSiteLocale()) {
  if (!href || locale === "en") return href;
  if (/^(?:[a-z][a-z\d+.-]*:|#|\/\/)/i.test(href)) return href;

  const [pathAndQuery, hash = ""] = href.split("#", 2);
  const [pathname, query = ""] = pathAndQuery.split("?", 2);
  if (!pathname.startsWith("/")) return href;
  if (pathname === GREEK_PREFIX || pathname.startsWith(`${GREEK_PREFIX}/`)) return href;
  if (NON_PUBLIC_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
    return href;
  }

  const localizedPath = pathname === "/" ? `${GREEK_PREFIX}/` : `${GREEK_PREFIX}${pathname}`;
  return `${localizedPath}${query ? `?${query}` : ""}${hash ? `#${hash}` : ""}`;
}

export function getLanguageSwitchHref(pathname?: string, search?: string, hash?: string) {
  const currentPath = pathname ?? (typeof window === "undefined" ? "/" : window.location.pathname);
  const currentSearch = search ?? (typeof window === "undefined" ? "" : window.location.search);
  const currentHash = hash ?? (typeof window === "undefined" ? "" : window.location.hash);
  const locale = getSiteLocale(currentPath);
  const targetPath = locale === "el" ? stripLocalePrefix(currentPath) : localizeHref(currentPath, "el");
  return `${targetPath}${currentSearch}${currentHash}`;
}

export function localizedCurrentPath(path: string) {
  return localizeHref(path, getSiteLocale());
}

export function localizedApiHref(href: string, locale: SiteLocale = getSiteLocale()) {
  const separator = href.includes("?") ? "&" : "?";
  return `${href}${separator}language=${encodeURIComponent(locale)}`;
}

export function applyDocumentLocale(locale: SiteLocale) {
  document.documentElement.lang = locale;
  document.documentElement.dataset.locale = locale;
}
